import { Component, forwardRef, Input, OnInit } from '@angular/core';
import {
    ControlValueAccessor,
    NG_VALUE_ACCESSOR,
    FormBuilder,
    FormGroup,
    FormArray,
    Validators,
    ReactiveFormsModule,
    FormsModule,
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { NgSelectModule } from '@ng-select/ng-select';
import { CoveragesService } from '../../services/coverages.service';
import { CoverageListDto } from '../../../../core/data/new-dtos/insurance-type.dtos';
import { lastValueFrom } from 'rxjs';
import { CoverageModalComponent, CoverageModalResult } from '../../../coverages/ui/coverage-modal/coverage-modal.component';

export interface CreateCoveragePlanItemDto {
    coverageName: string;
    coverageId: number;
    sumInsured: number;
    premium: number;
}

export interface CreateCoveragePlanDto {
    name: string;
    items: CreateCoveragePlanItemDto[];
}

@Component({
    selector: 'app-coverage-plan-selector',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        FormsModule,
        NgxMaskDirective,
        NgbTooltipModule,
        NgSelectModule,
        CoverageModalComponent,
    ],
    templateUrl: './coverage-plan-selector.component.html',
    styleUrl: './coverage-plan-selector.component.css',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => CoveragePlanSelectorComponent),
            multi: true,
        },
        provideNgxMask(),
    ],
})
export class CoveragePlanSelectorComponent implements ControlValueAccessor, OnInit {
    @Input() planType: string = 'Cobertura';
    form: FormGroup;
    availableCoverages: CoverageListDto[] = [];
    loading = false;
    showCoverageModal = false;
    activePlanIndex: number | null = null;
    activeItemIndex: number | null = null;

    private isWriteValue = false;

    constructor(
        private fb: FormBuilder,
        private coveragesService: CoveragesService,
    ) {
        this.form = this.fb.group({
            plans: this.fb.array([]),
        });

        this.form.valueChanges.subscribe((val) => {
            if (!this.isWriteValue) {
                this.onChange(val.plans);
                this.onTouched();
            }
        });
    }

    async ngOnInit() {
        await this.loadCoverages();
    }

    async loadCoverages() {
        this.loading = true;
        try {
            this.availableCoverages = await lastValueFrom(this.coveragesService.getAll());
        } catch (e) {
            console.error('Error loading coverages', e);
        } finally {
            this.loading = false;
        }
    }

    getCoverageId(planIndex: number, itemIndex: number): number | null {
        try {
            const item = this.getItems(planIndex).at(itemIndex).value;
            return item.coverageId || null;
        } catch (e) {
            return null;
        }
    }

    onCoverageSelect(planIndex: number, itemIndex: number, coverageId: number) {
        if (!coverageId) return;
        const coverage = this.availableCoverages.find(c => c.id === coverageId);
        if (coverage) {
            const itemControl = this.getItems(planIndex).at(itemIndex);
            itemControl.patchValue({
                coverageId: coverage.id,
                coverageName: coverage.name
            });
        }
    }

    openCoverageModal(planIndex: number, itemIndex: number) {
        this.activePlanIndex = planIndex;
        this.activeItemIndex = itemIndex;
        this.showCoverageModal = true;
    }

    async onCoverageModalClosed(result: CoverageModalResult) {
        this.showCoverageModal = false;
        if (result.action === 'save' && result.data) {
            this.loading = true;
            try {
                const newId = await lastValueFrom(
                    this.coveragesService.create(result.data as any),
                );
                await this.loadCoverages(); // Reloads this.availableCoverages

                if (this.activePlanIndex !== null && this.activeItemIndex !== null) {
                    this.onCoverageSelect(this.activePlanIndex, this.activeItemIndex, newId);
                }
            } catch (e) {
                console.error('Error creating coverage', e);
            } finally {
                this.loading = false;
                this.activePlanIndex = null;
                this.activeItemIndex = null;
            }
        } else {
            this.activePlanIndex = null;
            this.activeItemIndex = null;
        }
    }

    get plans(): FormArray {
        return this.form.get('plans') as FormArray;
    }

    getItems(planIndex: number): FormArray {
        return this.plans.at(planIndex).get('items') as FormArray;
    }

    createPlanGroup(plan?: CreateCoveragePlanDto): FormGroup {
        return this.fb.group({
            name: [plan?.name || '', Validators.required],
            items: this.fb.array(
                plan?.items?.map((item) => this.createItemGroup(item)) || [],
            ),
        });
    }

    createItemGroup(item?: CreateCoveragePlanItemDto): FormGroup {
        return this.fb.group({
            coverageName: [
                item?.coverageName || '',
                Validators.required,
            ],
            coverageId: [
                item?.coverageId || null,
                Validators.required,
            ],
            sumInsured: [
                item?.sumInsured || 0,
                [Validators.required, Validators.min(0)],
            ],
            premium: [
                item?.premium || 0,
                [Validators.required, Validators.min(0)],
            ],
        });
    }

    addPlan() {
        this.plans.push(this.createPlanGroup());
    }

    removePlan(index: number) {
        this.plans.removeAt(index);
    }

    addItem(planIndex: number) {
        this.getItems(planIndex).push(this.createItemGroup());
    }

    removeItem(planIndex: number, itemIndex: number) {
        this.getItems(planIndex).removeAt(itemIndex);
    }

    calculateTotalPremium(planIndex: number): number {
        const items = this.getItems(planIndex);
        let total = 0;
        for (let i = 0; i < items.length; i++) {
            total += Number(items.at(i).get('premium')?.value || 0);
        }
        return total;
    }

    calculateTotalSumInsured(planIndex: number): number {
        const items = this.getItems(planIndex);
        let total = 0;
        for (let i = 0; i < items.length; i++) {
            total += Number(items.at(i).get('sumInsured')?.value || 0);
        }
        return total;
    }

    writeValue(obj: any): void {
        if (obj && Array.isArray(obj)) {
            this.isWriteValue = true;
            this.plans.clear();
            obj.forEach((plan) => {
                this.plans.push(this.createPlanGroup(plan));
            });
            this.isWriteValue = false;
        }
    }

    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    setDisabledState?(isDisabled: boolean): void {
        if (isDisabled) {
            this.form.disable();
        } else {
            this.form.enable();
        }
    }

    private onChange: any = () => { };
    private onTouched: any = () => { };
}
